import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import Seo, { breadcrumbLd } from '../components/Seo';
import { MaskedLines, FadeUp, Eyebrow } from '../components/motion';
import QuoteForm from '../components/QuoteForm';
import NotFound from './NotFound';
import { useT } from '../i18n/LangContext';
import { useContent } from '../data/useContent';

export default function AreaPage() {
  const { slug } = useParams();
  const t = useT();
  const { AREAS, SERVICES } = useContent();
  const area = AREAS.find((a) => a.slug === slug);
  if (!area) return <NotFound />;

  return (
    <>
      <Seo title={`${t['ar.h1a']} ${area.name} | Reverence Services`} description={`${area.name} ${t['seo.area.desc']}`}
        jsonLd={[breadcrumbLd(['Home', 'Areas', area.name])]} />
      <section className="grain relative overflow-hidden bg-ink">
        <div className="hero-glow absolute inset-0" />
        <div className="container-site relative z-10 grid gap-14 py-24 md:py-32 lg:grid-cols-2 lg:items-center">
          <div>
            <Eyebrow>{t['ar.eyebrow']}</Eyebrow>
            <MaskedLines as="h1" className="mt-4 font-serif text-4xl font-semibold leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl"
              lines={[{ text: t['ar.h1a'], cls: 'text-white' }, { text: `${area.name}.`, cls: 'italic text-gold' }]} />
            <FadeUp delay={0.3}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-white/75 md:text-lg">{area.copy}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {SERVICES.map((s) => (
                  <Link key={s.slug} data-testid={`area-service-${s.slug}`} to={`/${s.slug}`}
                    className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-white/80 transition-colors hover:border-gold hover:text-gold">
                    {s.name} <ArrowUpRight className="h-3 w-3 opacity-50" />
                  </Link>
                ))}
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.2}>
            <QuoteForm variant="card" source={`area-${area.slug}`} idPrefix={`area-quote-${area.slug}`} />
          </FadeUp>
        </div>
      </section>

      <section className="bg-white py-20 text-center text-ink">
        <FadeUp>
          <h2 className="font-serif text-3xl font-semibold tracking-tight sm:text-4xl">{t['ar.h2a']} {area.name}? <span className="italic text-gold-dim">{t['ar.h2b']}</span></h2>
          <Link data-testid="area-quote-cta" to="/quote" className="gold-btn mt-6">{t['ar.cta']} <ArrowRight className="h-4 w-4" /></Link>
        </FadeUp>
      </section>
    </>
  );
}
